<div
  #chatMain
  id="chat-main"
  class="chat-main"
  tabindex="-1"
  (focusout)="chatFocusSubject$.next(false)"
  (focusin)="chatFocusSubject$.next(true)"
  (touchmove)="isMobile ? onTouchMove($event) : ''"
  (touchend)="isMobile ? onTouchEnd($event) : ''"
  (click)="detectChanges()"
>
  <div
    class="drop-box drop-box-transparent"
    [hidden]="!dragging && !draggingError"
    (dragleave)="dragLeave($event)"
    (dragover)="dragOver($event)"
    (dragenter)="dragOver($event)"
    (dragend)="dragLeave($event)"
    (drop)="drop($event)"
  ></div>

  <div [hidden]="!dragging && !draggingError" class="drop-box bg-surface-100 dark:bg-surface-600"></div>

  @if (dragging) {
    <div class="drop-box-container">
      <div class="drop-box-dialog">
        <i class="mi2-cloud-upload text-[128px]"></i>
      </div>
      <div class="drop-box-dialog-text">
        @if (currentConversation.stream) {
          <div class="drop-box-title" translate [translateParams]="{ name: currentConversation.name }">
            cl_chat.uploadFileToStreamConversation
          </div>
        }
        @if (!currentConversation.stream) {
          <div class="drop-box-title">
            {{ 'cl_chat.uploadFileToConversation' | translate }}
          </div>
        }
        <div class="drop-box-text" translate [translateParams]="{ size: maxFileSizeMb }">cl_chat.maxFileSizeMB</div>
      </div>
    </div>
  }

  @if (draggingError) {
    <div class="drop-box-container">
      <div class="drop-box-error-dialog">
        <button
          [attr.aria-label]="'cl_chat.close' | translate"
          class="exit-button"
          (click)="closeDraggingError()"
          data-e2e="base-modal-close-button"
        >
          <i class="mi2-close text-[20px] text-surface-600"></i>
        </button>
        <div class="py-8 px-8">
          <i class="mi2-report-issue text-[35px] text-red-600"></i>
        </div>
        <div class="drop-box-title">{{ 'cl_chat.fileTooLarge' | translate }}</div>
        <div class="drop-box-text" translate [translateParams]="{ size: maxFileSizeMb }">cl_chat.maxFileSizeMB</div>
        <div class="dialog-button-group">
          <button
            #dragDropOkBtn
            class="chat-one-app-button modal-button focus-button"
            (click)="closeDraggingError()"
            [ngClass]="primeThemeService.isDarkMode() ? 'primary-dark' : 'primary-grey'"
          >
            {{ 'cl_chat.ok' | translate }}
          </button>
        </div>
      </div>
    </div>
  }

  <div #dialogBoxes class="dialog-boxes" [ngClass]="{ 'header-dialog-boxes': config?.showHeader }">
    @if (!conversationId && !sdkError() && !config?.hideNoConversationIdError) {
      <div class="error-box" role="alert" [@fadeInOut]>
        <div>{{ 'cl_chat.noConversationId' | translate }}</div>
      </div>
    }
    @if (sdkError()) {
      <div class="error-box" role="alert" [@fadeInOut]>
        <div>
          {{ 'cl_chat.failedToInitialize' | translate }}
          {{ 'cl_chat.problemCommunicatingWithChat' | translate }}
        </div>
      </div>
    }
    @if (subscriptionError() && !sdkError() && !isDisconnected() && !isOffline()) {
      <div class="error-box permanent-error" role="alert" [@fadeInOut]>
        <div>{{ 'cl_chat.problemCommunicatingWithChat' | translate }}</div>
        <button type="button" class="retry-button" (click)="retrySubscriptions()">
          {{ 'cl_chat.retry' | translate }}
        </button>
      </div>
    }
    @if (error()) {
      <div class="error-box one-time-error" role="alert" [@fadeInOut]>
        <div>{{ error() }}</div>
        @if (!hideCloseError()) {
          <button
            type="button"
            class="button-remove-style"
            [attr.aria-label]="'cl_chat.close' | translate"
            (click)="clearError()"
          >
            <i class="mi2-close text-[12px] text-surface-0 mr-2"></i>
          </button>
        }
      </div>
    }
    @if ((config?.showHostErrors || config?.showDisconnectedHostError) && isDisconnected() && !isOffline()) {
      <div class="disconnected-box" role="status" [@fadeInOut]>
        <div>{{ 'cl_chat.tryingToConnect' | translate }}</div>
      </div>
    }
    @if ((config?.showHostErrors || config?.showOfflineHostError) && isOffline()) {
      <div class="offline-box" role="status" [@fadeInOut]>
        <div>{{ 'cl_chat.deviceOfflineFunctionalityLimited' | translate }}</div>
      </div>
    }
    @if (noDbConversation()) {
      <div class="error-box" role="alert" [@fadeInOut]>
        <div>{{ 'cl_chat.conversationNotCached' | translate }}</div>
      </div>
    }
    @if (hasUnreadMessage && !loading() && !contentLoading()) {
      <div class="unread-box" role="status" [@fadeInOut]>
        <button
          class="button-remove-style"
          [attr.aria-label]="'cl_chat.scrollToBottom' | translate"
          (click)="scrollingService.scrollToBottom()"
        >
          <i class="mi2-arrow-down text-[11px] text-surface-0 ml-2"></i>
        </button>
        <div>{{ unreadMessageString }}</div>
        <button
          type="button"
          class="button-remove-style"
          [attr.aria-label]="'cl_chat.close' | translate"
          (click)="updateLastReadMessageDate(messages[messages.length - 1])"
        >
          <i class="mi2-close text-[11px] text-surface-0 mr-2"></i>
        </button>
      </div>
    }
    @if (infoDialogText) {
      <div class="info-box" role="status" [@fadeInOut]>
        <div>{{ infoDialogText }}</div>
      </div>
    }
    @if (warningDialogText) {
      <div class="warning-box" role="status" [@fadeInOut]>
        <div>{{ warningDialogText }}</div>
      </div>
    }
  </div>

  @if (config?.showHeader && !aiMode) {
    <mi-header
      #header
      (dragover)="dragOver($event)"
      (dragenter)="dragOver($event)"
      [fontSize]="config?.headerFontSize ?? 24"
      [headerHeight]="config?.headerHeight ?? 63"
      [showBackButton]="config?.headerShowBackButton ? config.headerShowBackButton : false"
      [showSearch]="config?.headerShowSearch ? config.headerShowSearch : false"
      [title]="config?.headerTitle != null ? config.headerTitle : currentConversation.name"
      [buttons]="config?.headerButtons ? config.headerButtons : []"
      (onButtonClick)="headerButtonClick($event)"
      (onSearch)="headerSearch($event)"
      [(search)]="search"
      [buttonTooltipPlacement]="'left'"
    >
    </mi-header>
  }
  <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
    {{ newMessageAnnouncement() }}
  </div>
  <div
    class="main-content"
    #maincontent
    id="maincontent"
    (dragover)="dragOver($event)"
    (dragenter)="dragOver($event)"
    [ngClass]="{ 'main-read-only': readOnly, 'main-hidden': contentLoading() }"
    [style.--banner-stack-height.px]="bannerStackHeight()"
  >
    <div #chatList id="chat-list" class="chat-list">
      @if (isSearch && messages.length === 0 && !contentLoading()) {
        <div class="no-search-results">
          {{ 'cl_chat.noResultsFound' | translate }}
        </div>
      }
      @for (item of messages; track trackByFn(i, item); let i = $index) {
        <div>
          @if (item.clCompChat_firstUnread) {
            <div
              class="flex w-full text-xs font-semibold text-primary-500 mt-4"
              [ngClass]="{ 'unread-continuation': isContinuationMessage(i, item) }"
            >
              <div class="h-px grow mt-3.5 bg-surface-950 dark:bg-surface-500"></div>
              <div class="border border-surface-950 dark:border-surface-500 py-1.5 px-2.5 rounded-[2.5rem]">
                {{ 'cl_chat.newMessages' | translate }}
                <span class="ml-3.5 text-color uppercase">{{ firstUnreadMessageDateString }}</span>
              </div>
              <div class="h-px grow mt-3.5 bg-surface-950 dark:bg-surface-500"></div>
            </div>
          }
          @if (!item.clCompChat_hidden && item.clCompChat_isStartOfDay && !item.clCompChat_firstUnread) {
            <div class="flex items-center gap-1 w-full text-xs font-semibold text-primary-500 mt-4">
              <div class="h-px grow bg-surface-950 dark:bg-surface-500"></div>
              <div
                class="flex h-9 py-[7px] px-[21px] justify-center items-center gap-[3.5px] self-stretch bg-content-background border border-surface-950 dark:border-surface-500 rounded-[2.5rem] text-color uppercase"
              >
                {{ item.createdOn | chatDate: dateTimeLocale : config?.showDayOfWeek }}
              </div>
              <div class="h-px grow bg-surface-950 dark:bg-surface-500"></div>
            </div>
          }
          @if ((!item.clCompChat_hidden && !isContinuationMessage(i, item)) || isSearch) {
            <div class="chat-start-item"></div>
          }
          <chat-item
            class="chat-item"
            [chatItems]="chatItems"
            [id]="item.messageId"
            [loading]="loading() || contentLoading()"
            [isMessageInfoOpen]="isMessageInfoOpen"
            [isSnapshot]="isSnapshot"
            [isSearch]="isSearch"
            [item]="item"
            [aiMode]="aiMode"
            [showAiQuickReplies]="showAiQuickReplies(item, $last)"
            [aiQuickRepliesDisabled]="textAreaDisabled"
            [aiHandoffLabel]="aiHandoffLabel"
            [previewMode]="previewMode"
            [textAreaDisabled]="textAreaDisabled"
            [isOffline]="isOffline()"
            [isContinuation]="isContinuationMessage(i, item)"
            [goingToQuoted]="goingToQuoted"
            [config]="config"
            [smsMode]="config?.smsMode"
            [smsMessage]="item.clCompChat_smsMessage"
            [reactions]="reactions"
            [downloadAttachmentOverride]="downloadAttachmentOverride"
            [fullScreenPdfOverride]="fullScreenPdfOverride"
            [maxImageSize]="maxImageSize"
            [noReadIndicators]="isLargeParticipantLoad || config?.hideReadIndicators || aiMode"
            [dropdownMaxHeight]="dropdownMaxHeight"
            [emojiDictionary]="emojiDictionary"
            [dateTimeLocale]="dateTimeLocale"
            [isRecording]="isRecording"
            [participants]="participants"
            [(editModeOpen)]="editModeOpen"
            [(currentEditMessageId)]="currentEditMessageId"
            [currentMessageInfoId]="currentMessageInfoId"
            (onSelectMessagesMode)="activateSelectMessagesMode($event)"
            [selectMessagesMode]="selectMessagesMode"
            (onMessageSelected)="onSelectRemoveMessage($event, item)"
            (onChatItemActionCardMessage)="send($event.message, $event.fallbackText, $event.throwError)"
            (onChatItemActionCardEvent)="onActionCardEvent.emit($event)"
            (onWindowOpen)="onWindowOpen.emit($event)"
            (onGetCurrentLocation)="getCurrentLocation($event.throwError, $event.sendLocation)"
            (onGoToMessage)="goToMessage($event)"
            (onGoToAiSuggestionsMessage)="onGoToAiSuggestionsMessage.emit($event)"
            (onAiQuickReply)="onAiQuickReply($event)"
            (onAiHandoff)="onAiHandoff()"
            (onShowWarning)="showWarning($event)"
            (onNameClicked)="nameClicked($event)"
            (onMessageContextMenu)="onMessageContextMenu.emit($event)"
            (onRetrySend)="retrySend($event)"
            (onDeletePendingSend)="deletePendingSend($event)"
            (onFullScreenPdf)="fullScreenPdf($event)"
            (onFullScreenImage)="fullScreenImage($event)"
            (onAvatarMouseEnter)="onAvatarMouseEnter.emit($event)"
            (onAvatarMouseLeave)="onAvatarMouseLeave.emit($event)"
            (onAvatarContextMenu)="onAvatarContextMenu.emit($event)"
            (onChatItemActionCardBehavior)="onActionCardBehavior($event)"
            (onChatItemOpenLink)="onOpenLink($event)"
            (onChatItemPostWebhookPayload)="onPostWebhookPayload($event)"
            (onChatItemSendWebhookPayload)="onSendWebhookPayload($event)"
            (onOpenModal)="onOpenModal($event)"
            (onQuotedMessageChange)="quotedMessageChange($event)"
          >
          </chat-item>
        </div>
      }
      <div class="chat-list-buffer"></div>
    </div>
  </div>
  @if (contentLoading()) {
    <div class="loading-screen content-loading" data-e2e="loading-screen">
      <ng-container *ngTemplateOutlet="loadingTemplate"> </ng-container>
    </div>
  }
  @if (isSnapshot && !contentLoading()) {
    <button type="button" class="exit-snapshot" (click)="goToLatest()">
      <div class="exit-name">{{ 'cl_chat.goToLatest' | translate }}</div>
    </button>
  }
  @if (isSearch && !contentLoading() && config?.showExitSearchButton) {
    <div class="exit-snapshot">
      <button type="button" class="exit-area" (click)="goToLatest()">
        <div class="exit-name">{{ 'cl_chat.exitSearch' | translate }}</div>
      </button>
    </div>
  }
  @if (isMobile && !selectMessagesMode) {
    <div
      class="typing-content"
      role="status"
      [ngClass]="{
        'safari-typing-content': chatCloudLinkService?.browser?.name === 'safari'
      }"
      [hidden]="remoteTyping.length === 0 && !syncing() && !uploading"
    >
      <span class="typing-text" [hidden]="remoteTyping.length === 0" [innerHTML]="getRemoteText()"></span>
      <span class="typing-text" [hidden]="!syncing()">{{ 'cl_chat.syncing' | translate }}</span>
      <span data-e2e="typing-text" class="typing-text" [hidden]="!uploading">{{
        'cl_chat.uploading' | translate
      }}</span>
    </div>
  }
  @if (oneDriveUploads().length) {
    <ul class="list-none m-0 px-4 py-1 flex flex-col gap-1" data-e2e="onedrive-uploads">
      @for (u of oneDriveUploads(); track u.id) {
        <li class="flex items-center gap-2 text-xs text-surface-700 dark:text-surface-200">
          <i class="mi2-cloud-upload text-base shrink-0" aria-hidden="true"></i>
          <span class="ellipsis-overflow min-w-0 flex-1">{{
            'cl_chat.oneDriveUploading' | translate: { fileName: u.fileName }
          }}</span>
          <div
            class="w-24 h-1 rounded-full overflow-hidden shrink-0 bg-surface-200 dark:bg-surface-700"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            [attr.aria-valuenow]="u.progress * 100 | number: '1.0-0'"
            [attr.aria-label]="'cl_chat.oneDriveUploading' | translate: { fileName: u.fileName }"
          >
            <div class="h-full bg-primary" [style.width.%]="u.progress * 100"></div>
          </div>
          <span class="w-9 text-right tabular-nums shrink-0" aria-hidden="true"
            >{{ u.progress * 100 | number: '1.0-0' }}%</span
          >
          <mi-round-button
            size="sm"
            icon="mi2-close"
            [ariaLabel]="'cl_chat.oneDriveCancelUpload' | translate: { fileName: u.fileName }"
            [tooltip]="'cl_chat.oneDriveCancelUpload' | translate: { fileName: u.fileName }"
            tooltipPosition="top"
            (clicked)="cancelOneDriveUpload(u)"
            data-e2e="onedrive-upload-cancel"
          ></mi-round-button>
        </li>
      }
    </ul>
  }
  @if (!readOnly && !previewMode && !config?.jsonMessagesAsSource) {
    <div class="footer" (dragover)="dragOver($event)" (dragenter)="dragOver($event)">
      @if (!config || (!config.hideCommands && !config?.smsMode)) {
        <div>
          <span id="commandDropup" style="display: none"></span>
          <p-menu
            #commandsDropdown
            id="commands-dropdown"
            [model]="textAreaProps.commands"
            [popup]="true"
            (onShow)="restoreTextAreaFocus()"
            styleClass="command-dropdown"
            [ngClass]="{ 'mobile-dropdown': isMobile }"
            appendTo="body"
          >
            <ng-template pTemplate="item" let-item let-i="index">
              <div
                class="command-item"
                #dropdownCommands
                [ngClass]="{
                  isSelected: !isMobile && i === textAreaProps.selectedDropdownIndex
                }"
                (click)="enterCommand(item)"
              >
                <div class="command-title ellipsis-overflow">
                  <span>
                    <b>/{{ item.commandId }}</b>
                  </span>
                  @for (param of item.parameters; track param.parameterId) {
                    <span>
                      @if (!param.hidden) {
                        <span>&nbsp;[{{ param.parameterId }}]</span>
                      }
                    </span>
                  }
                </div>
                <div class="command-description ellipsis-overflow">
                  <span>{{ item.description }}</span>
                </div>
              </div>
            </ng-template>
          </p-menu>
        </div>
      }
      @if (!config || (!config.hideMentions && !config?.smsMode)) {
        <div>
          <span #mentionsAnchor style="position: fixed; left: 0; top: 0; width: 0; height: 0; z-index: -1"></span>
          <p-menu
            #mentionsDropdown
            id="mentions-dropdown"
            [model]="textAreaProps.mentions"
            [popup]="true"
            (onShow)="restoreTextAreaFocus()"
            appendTo="body"
            styleClass="mentions-dropdown"
            [ngClass]="{ 'mobile-dropdown': isMobile }"
          >
            <ng-template pTemplate="item" let-item let-i="index">
              <div
                class="mentions-item"
                #dropdownMentions
                [ngClass]="{
                  'mentions-mobile-item': isMobile,
                  isSelected: !isMobile && i === textAreaProps.selectedDropdownIndex
                }"
                (click)="enterMention(item)"
              >
                @if (item.userId !== 'here') {
                  <mi-avatar
                    [hash]="item.userId | hash"
                    [name]="item.displayName"
                    [initials]="item.displayName | initials"
                    [photoUrl]="item.avatars && item.avatars[50] ? item.avatars[50] : ''"
                  >
                  </mi-avatar>
                }
                @if (item.userId === 'here') {
                  <div class="flex items-center justify-center min-w-[35px] min-h-[35px]">
                    <i class="mi2-speaker-on text-lg"></i>
                  </div>
                }
                <div class="ellipsis-overflow">
                  @if (!config || !config.reverseMentionSearchNames) {
                    <span class="mention-name">
                      {{ item.displayName }}
                    </span>
                  }
                  @if (config?.reverseMentionSearchNames) {
                    <span class="mention-name">
                      {{ item.displayName.split(' ').reverse().join(' ') }}
                    </span>
                  }
                  <span class="mention-email">
                    {{ item.email }}
                  </span>
                </div>
              </div>
            </ng-template>
          </p-menu>
        </div>
      }
      @if (selectMessagesMode) {
        <div class="selectMessagesMode-button-group">
          <button
            data-e2e="cancel-select-button"
            class="chat-cancel-button chat-one-app-button modal-button"
            [ngClass]="primeThemeService.isDarkMode() ? 'primary-dark' : 'primary-grey'"
            (click)="activateSelectMessagesMode(false)"
          >
            {{ 'cl_chat.cancel' | translate }}
          </button>
          @if (messageDeleteList.length === 1) {
            <div>
              {{ 'cl_chat.messageSelected' | translate: { number: messageDeleteList.length } }}
            </div>
          }
          @if (messageDeleteList.length != 1) {
            <div>
              {{ 'cl_chat.messagesSelected' | translate: { number: messageDeleteList.length } }}
            </div>
          }
          <button
            class="chat-delete-button chat-one-app-button modal-button primary-red"
            (click)="deleteMessages()"
            data-e2e="delete-selected-button"
            [disabled]="messageDeleteList.length === 0"
            [ngStyle]="{ 'box-shadow': 'var(--p-mi-shadow)' }"
          >
            {{ 'cl_chat.deleteMessage' | translate }}
          </button>
        </div>
      }
      @if (!selectMessagesMode) {
        <div
          class="text-area-container"
          [ngClass]="{
            'stacked-text-area': stackedTextArea,
            'mobile-text-area': isMobile,
            'text-area-attachment': pendingAttachments.length,
            'text-area-reply': quotedMessage,
            'text-area-attachment-reply': pendingAttachments.length && quotedMessage,
            'text-area-recording': isRecording || currentRecording
          }"
        >
          <div
            class="text-area"
            id="text-area"
            #textArea
            [attr.contenteditable]="!!conversationId && (!textAreaDisabled || offlineSendingEnabled)"
            data-e2e="text-area"
            role="textbox"
            aria-multiline="true"
            [attr.aria-label]="'cl_chat.typeAMessageNew' | translate"
            [attr.spellcheck]="config && config.disableSpellcheck ? false : true"
            [attr.placeholder]="'cl_chat.typeAMessageNew' | translate"
            [attr.aria-haspopup]="suggestionsDropdownId ? 'menu' : null"
            [attr.aria-controls]="suggestionsDropdownId"
            (input)="onInput($event)"
            (mouseup)="getCaretPosition()"
            (keypress)="keyPressSubject.next($event)"
            (keyup)="keyUpSubject.next($event)"
            (paste)="onPaste($event)"
            (cut)="isMobile ? updateMobileSendButton() : ''"
            (delete)="isMobile ? updateMobileSendButton() : ''"
            (drop)="onDrop($event)"
            (keydown)="keyDownSubject.next($event)"
            (blur)="onBlur($event)"
            (focus)="onFocus()"
            [ngClass]="{
              'text-area-disabled': textAreaDisabled && !offlineSendingEnabled
            }"
            [ngStyle]="{ 'padding-right': textAreaPaddingRight / 14 + 'rem' }"
          ></div>
          <!-- recording audio dialog -->
          @if (isRecording || currentRecording) {
            <div
              class="record-audio-dialog"
              [ngClass]="{ 'record-audio-dialog-mobile': isMobile }"
              [ngStyle]="{ width: textArea.offsetWidth / 14 + 'rem', top: quotedMessage ? '4.5rem' : '0.625rem' }"
            >
              <div class="record-audio-content">
                @if (isRecording) {
                  <div>
                    <i class="mi2-voice-on text-xl"></i>
                    <div class="record-time">{{ recordingTime }}</div>
                  </div>
                }
                @if (!isRecording && currentRecording) {
                  <div class="current-recording" data-e2e="current-recording">
                    <audio style="display: none" controls #audio preload="none" (timeupdate)="audioUpdateTime()">
                      <source src="" type="audio/wav" />
                    </audio>
                    <button
                      #playRecordingBtn
                      class="record-playing-button"
                      role="button"
                      [attr.aria-label]="getAudioButtonText()"
                      [pTooltip]="getAudioButtonText()"
                      (click)="playRecording()"
                      tooltipPosition="top"
                    >
                      @if (audioObj.audioPlaying) {
                        <div class="record-playing-button-pause">
                          <i class="mi2-pause-circle text-base text-primary"></i>
                        </div>
                      }
                      @if (!audioObj.audioPlaying) {
                        <div class="record-playing-button-play">
                          <i class="mi2-play text-base text-primary"></i>
                        </div>
                      }
                    </button>
                    @if (textArea.offsetWidth > minWidthForWaveforms) {
                      <div class="record-time">{{ audioObj.audioStart }}</div>
                    }
                    @if (textArea.offsetWidth > minWidthForWaveforms) {
                      <div class="audio-waveforms" [style.visibility]="currentRecording ? 'visible' : 'hidden'">
                        <canvas #waveformsCanvas></canvas>
                        <div
                          class="audio-progress"
                          [ngStyle]="{ width: audioObj.audioProgress }"
                          [style.visibility]="audioObj.audioProgress != '0%' ? 'visible' : 'hidden'"
                        >
                          <div class="canvas-container" #progressCanvas></div>
                        </div>
                      </div>
                    }
                    <div class="record-time">{{ recordingTime }}</div>
                    <button
                      [attr.aria-label]="'cl_chat.delete' | translate"
                      (click)="removeRecording()"
                      class="record-remove"
                      [pTooltip]="translate.instant('cl_chat.delete')"
                    >
                      <i class="mi2-trash text-base text-primary"></i>
                    </button>
                  </div>
                }
              </div>
            </div>
          }
          <!-- attachments -->
          @if (pendingAttachments.length) {
            <div
              class="attachment-pane"
              [ngClass]="{
                'stacked-attachment-pane': stackedTextArea,
                'attachment-pane-reply': pendingAttachments.length && quotedMessage,
                'attachment-pane-mobile': isMobile
              }"
              [ngStyle]="{
                top: quotedMessage
                  ? isRecording || currentRecording
                    ? '6.5rem'
                    : '3.5rem'
                  : isRecording || currentRecording
                    ? '2.5rem'
                    : '0'
              }"
            >
              @for (a of pendingAttachments; track $index) {
                <div
                  class="attachment-pane-container"
                  [ngClass]="{ 'attachment-pane-file-container': !a.imageSafeUrl }"
                >
                  @if (a.imageSafeUrl) {
                    <img [src]="a.imageSafeUrl" draggable="false" alt="" />
                  }
                  @if (!a.imageSafeUrl) {
                    <div>
                      <mi-attachment-icon [attachmentName]="a.fileName"></mi-attachment-icon>
                    </div>
                  }
                  @if (!a.imageSafeUrl) {
                    <div class="attachment-file-info">
                      <span
                        #fileNameEl
                        [pTooltip]="fileNameEl.scrollWidth > fileNameEl.clientWidth ? a.fileName : ''"
                        class="attachment-file-name ellipsis-overflow"
                        >{{ a.fileName }}</span
                      >
                      <span class="attachment-file-size">{{ 'cl_chat.fileSize' | translate }}: {{ a.fileSize }}</span>
                      @if (a.oneDrive) {
                        <span class="attachment-file-size flex items-center gap-1" data-e2e="onedrive-pending">
                          <i class="mi2-shared" aria-hidden="true"></i>{{ 'cl_chat.oneDriveSharedAsLink' | translate }}
                        </span>
                      }
                    </div>
                  }
                  <button
                    class="attachment-pane-close button-remove-style"
                    (click)="clearCopiedAttachment(a)"
                    [attr.aria-label]="'cl_chat.close' | translate"
                  >
                    <i class="mi2-close-circle text-xl text-surface-950 dark:text-surface-0"></i>
                  </button>
                </div>
              }
            </div>
          }
          <!-- quotes -->
          @if (quotedMessage) {
            <div class="quote-content" [ngClass]="{ 'quote-content-mobile': isMobile }">
              <div class="quote-left-border"></div>
              <div
                class="quote-area"
                role="button"
                tabindex="0"
                (click)="scrollingService.scrollToMessage(quotedMessage.messageId)"
                (keydown.enter)="scrollingService.scrollToMessage(quotedMessage.messageId)"
                (keydown.space)="$event.preventDefault(); scrollingService.scrollToMessage(quotedMessage.messageId)"
              >
                <div class="quote-body">
                  <div #quotedisplay [innerHTML]="quotedMessageDisplay | linky"></div>
                </div>
                <span class="quote-name"
                  >{{ getDisplayName(quotedMessage.createdBy) }}
                  @if (!isMobile) {
                    <span class="quote-time">{{ quotedMessage.createdOn | unreadMessageDate: locale }}</span>
                  }
                </span>
                <button
                  class="quote-close button-remove-style"
                  [attr.aria-label]="'cl_chat.close' | translate"
                  (click)="undoQuotedMessage()"
                >
                  <i class="mi2-close-circle text-xl text-surface-950 dark:text-surface-0"></i>
                </button>
              </div>
            </div>
          }
          <!-- buttons -->
          <div
            class="text-area-buttons"
            [ngClass]="{
              'text-area-buttons-stacked': stackedTextArea,
              'text-area-buttons-mobile': isMobile
            }"
          >
            @if (config?.showCharCounter && message.length > 0) {
              <div [ngClass]="{ 'mobile-char-counter': isMobile }" class="char-counter">
                {{ message.length }} / {{ textAreaProps.maxInputCharacters }}
              </div>
            }
            <!-- record video -->
            @if (config?.showShareVideo && !isMobile) {
              <div
                [ngClass]="{ 'text-area-disabled': textAreaDisabled }"
                class="share-video-button"
                data-e2e="share-video-button"
              >
                <button
                  class="button-remove-style"
                  tabindex="0"
                  [disabled]="textAreaDisabled && !dropdownVisible['video_recording']"
                  [attr.aria-label]="'cl_chat.sendVideoMessage' | translate"
                  (click)="recordVideoPopover.show($event); startVideoPreview()"
                >
                  <i
                    class="mi2-video text-[20px] text-surface-600 dark:text-surface-0"
                    [pTooltip]="'cl_chat.sendVideoMessage' | translate"
                    tooltipPosition="top"
                    [ngClass]="{ 'text-area-disabled': textAreaDisabled }"
                  ></i>
                </button>
                <p-popover
                  #recordVideoPopover
                  (onShow)="dropdownChange(true, 'video_recording')"
                  (onHide)="dropdownChange(false, 'video_recording')"
                  styleClass="[&_.p-popover-content]:p-0"
                >
                  <div class="recording-container">
                    <div class="record-video-controls">
                      @if (!isVideoRecording && !currentVideoRecording && dropdownVisible['video_recording']) {
                        <button class="action" (click)="startVideoRecording()" tabindex="0">
                          <i
                            class="mi2-record text-[20px] text-surface-600 dark:text-surface-0"
                            [ngClass]="{ 'text-area-disabled': textAreaDisabled }"
                          ></i>
                        </button>
                      }
                      @if (isVideoRecording) {
                        <button class="action" (click)="stopVideoRecording()" tabindex="0">
                          <i class="mi2-record text-[20px] text-red-600"></i>
                        </button>
                      }
                      @if (!isVideoRecording && !!currentVideoRecording) {
                        <button
                          class="action chat-one-app-button primary-blue"
                          (click)="sendVideoRecording()"
                          tabindex="0"
                        >
                          <i class="mi2-send text-[20px] text-surface-0"></i>
                        </button>
                      }
                      @if (!isVideoRecording && !!currentVideoRecording) {
                        <button class="action" (click)="removeVideoRecording()" tabindex="0">
                          <i class="mi2-trash text-[20px] text-surface-950 dark:text-surface-0"></i>
                        </button>
                      }
                    </div>
                    <video #recordVideo autoplay></video>
                  </div>
                </p-popover>
              </div>
            }
            <!-- record audio  -->
            @if ((!config || !config.hideShareAudio) && noMobileTextOrAttachments() && !config?.smsMode) {
              <div
                #recordAudio
                [ngClass]="{
                  'text-area-disabled': ((textAreaDisabled || subscriptionError()) && !isRecording) || currentRecording,
                  'recording-active-red': isRecording && !currentRecording
                }"
                class="share-audio-button"
                data-e2e="share-audio-button"
              >
                <button
                  #audioRecordBtn
                  appDebounceClick
                  class="button-remove-style"
                  [attr.aria-label]="
                    isRecording
                      ? translate.instant('cl_chat.clickToStopRecording')
                      : translate.instant('cl_chat.clickToRecord')
                  "
                  [disabled]="((textAreaDisabled || subscriptionError()) && !isRecording) || currentRecording"
                  (debounceClick)="isRecording ? stopRecording() : startRecording()"
                  tabindex="0"
                  [pTooltip]="
                    isRecording
                      ? translate.instant('cl_chat.clickToStopRecording')
                      : translate.instant('cl_chat.clickToRecord')
                  "
                  tooltipPosition="top"
                >
                  <i
                    class="mi2-voice-on text-xl"
                    [ngClass]="{
                      'text-area-disabled':
                        ((textAreaDisabled || subscriptionError()) && !isRecording) || currentRecording
                    }"
                  ></i>
                </button>
              </div>
            }
            <!-- location dropdown -->
            @if (
              !aiMode &&
              (!config || !config.hideShareLocation) &&
              !(message && message.trim() != '' && isMobile) &&
              !(pendingAttachments.length && isMobile) &&
              !config?.smsMode
            ) {
              <div
                class="share-location-button"
                [ngClass]="{ 'text-area-disabled': textAreaDisabled }"
                data-e2e="share-location-button"
              >
                <button
                  #locationBtn
                  [ngClass]="{ 'text-area-disabled': textAreaDisabled }"
                  (click)="onLocationClick($event)"
                  class="button-remove-style"
                  [attr.aria-label]="'cl_chat.sendYourLocation' | translate"
                  [disabled]="textAreaDisabled"
                  tabindex="0"
                  [pTooltip]="'cl_chat.sendYourLocation' | translate"
                  tooltipPosition="top"
                  [hideDelay]="isMobile ? 500 : 0"
                >
                  <i class="mi2-location-pin text-xl" [ngClass]="{ 'text-area-disabled': textAreaDisabled }"></i>
                </button>
              </div>
            }
            <!-- attachment button -->
            @if (!config?.hideAddFiles && !config?.smsMode) {
              <div class="attachment-button" [ngClass]="{ 'text-area-disabled': textAreaDisabled }">
                @if (!config?.hideAddFiles) {
                  <button
                    (click)="textAreaDisabled || subscriptionError() ? '' : onAttachClick($event)"
                    [ngClass]="{
                      'text-area-disabled': textAreaDisabled || subscriptionError(),
                      'rounded-full bg-surface-200 dark:bg-surface-700 shadow-[0_0_0_7px_var(--p-surface-200)] dark:shadow-[0_0_0_7px_var(--p-surface-700)]':
                        attachMenuOpen
                    }"
                    class="button-remove-style"
                    tabindex="0"
                    [attr.aria-haspopup]="oneDriveAttachAvailable() ? 'menu' : null"
                    [attr.aria-expanded]="oneDriveAttachAvailable() ? attachMenuOpen : null"
                    [attr.aria-controls]="attachMenuOpen ? 'attach-menu' : null"
                    data-e2e="attach-button"
                    [attr.aria-label]="'cl_chat.sendFile' | translate"
                    [disabled]="textAreaDisabled || subscriptionError()"
                    [pTooltip]="'cl_chat.sendFile' | translate"
                    tooltipPosition="top"
                  >
                    <i
                      class="mi2-attachment text-[20px] text-surface-950 dark:text-surface-0"
                      [ngClass]="{ 'text-area-disabled': textAreaDisabled || subscriptionError() }"
                    ></i>
                  </button>
                  @if (config?.enableOneDriveLargeFiles) {
                    <app-attach-menu
                      #attachMenu
                      [maxFileSizeMb]="maxFileSizeMb"
                      (selected)="uploadFileClick($event)"
                      (openChange)="onAttachMenuOpenChange($event)"
                    ></app-attach-menu>
                  }
                }
              </div>
            }
            <!-- emojis dropdown -->
            @if (!aiMode && !config?.hideEmojis && !isMobile) {
              <div
                class="emoji-button"
                [ngClass]="{
                  'text-area-disabled': textAreaDisabled && !offlineSendingEnabled
                }"
              >
                <button
                  class="button-remove-style"
                  tabindex="0"
                  [attr.aria-label]="'cl_chat.sendEmoji' | translate"
                  [disabled]="textAreaDisabled && !offlineSendingEnabled"
                  [pTooltip]="'cl_chat.sendEmoji' | translate"
                  tooltipPosition="top"
                  (click)="onEmojiDropdownClick($event)"
                >
                  <i
                    class="mi2-happy-face text-2xl"
                    [ngClass]="{ 'text-area-disabled': textAreaDisabled && !offlineSendingEnabled }"
                  ></i>
                </button>
                <p-popover
                  #emojisDropdown
                  [style]="{
                    width: textArea.offsetWidth < 400 ? textArea.offsetWidth / 14 + 'rem' : '25rem',
                    border: 'none',
                    'box-shadow': 'none',
                    background: 'transparent'
                  }"
                  styleClass="[&_.p-popover-content]:p-0 [&::before]:hidden [&::after]:hidden"
                  (onShow)="dropdownChange(true, 'emoji')"
                  (onHide)="dropdownChange(false, 'emoji')"
                >
                  <emoji-picker
                    (emojiOutput)="onEmojiClick($event)"
                    [enableTwemoji]="config?.enableTwemoji"
                  ></emoji-picker>
                </p-popover>
              </div>
            }
            <!-- send -->
            <button
              data-e2e="send-button"
              [attr.aria-label]="'cl_chat.send' | translate"
              [pTooltip]="'cl_chat.send' | translate"
              tooltipPosition="top"
              class="chat-one-app-button send-button"
              suppressEvents
              (onClick)="!textAreaDisabled || offlineSendingEnabled ? sendButtonClicked() : ''"
              (keydown.enter)="!textAreaDisabled || offlineSendingEnabled ? sendButtonClicked() : ''"
              (keydown.space)="!textAreaDisabled || offlineSendingEnabled ? sendButtonClicked() : ''"
              [ngClass]="{
                'mobile-send-button': isMobile,
                'primary-green': config?.usePrimaryGreen,
                'primary-blue': !config?.usePrimaryGreen
              }"
              [disabled]="
                textAreaDisabled ||
                subscriptionError() ||
                ((!message || message.trim() === '' || message === '\n') &&
                  !pendingAttachments.length &&
                  !currentRecording &&
                  !textAreaHasTwemoji)
              "
              tabindex="0"
              [ngStyle]="{ 'box-shadow': 'var(--p-mi-shadow)' }"
            >
              <i
                class="mi2-send text-xl text-surface-0"
                [ngClass]="{
                  'text-area-disabled': textAreaDisabled && !offlineSendingEnabled
                }"
              ></i>
            </button>
          </div>
        </div>
      }
    </div>
  }

  @if (!isMobile && !selectMessagesMode) {
    <div
      class="typing-content"
      role="status"
      [ngClass]="{
        'safari-typing-content': chatCloudLinkService?.browser?.name === 'safari'
      }"
    >
      <span class="typing-text" [hidden]="remoteTyping.length === 0" [innerHTML]="getRemoteText()"></span>
      <span class="typing-text" [hidden]="!syncing()">{{ 'cl_chat.syncing' | translate }}</span>
      <span data-e2e="uploading-text" class="typing-text" [hidden]="!uploading">{{
        'cl_chat.uploading' | translate
      }}</span>
    </div>
  }
  @if (loading()) {
    <div class="loading-screen main-loading-screen" data-e2e="main-loading-screen">
      <ng-container *ngTemplateOutlet="loadingTemplate"> </ng-container>
    </div>
  }
  @if (isFullScreen) {
    <div class="full-screen-background" [ngClass]="{ 'full-screen-in-chat': config?.fullScreenInChat }"></div>
  }
  @if (isFullScreenImage) {
    <div class="full-screen-content" [ngClass]="{ 'full-screen-in-chat': config?.fullScreenInChat }">
      <div class="full-screen-top">
        <ng-container *ngTemplateOutlet="fullScreenAvatarTemplate"> </ng-container>
        <button
          #fullScreenClose
          class="button-remove-style full-screen-close"
          [attr.tabindex]="fullScreenId ? 2 : 1"
          [attr.aria-label]="'cl_chat.close' | translate"
          (click)="exitFullScreen()"
        >
          <i class="mi2-close text-xl text-surface-950 dark:text-surface-0"></i>
        </button>
        <div class="cursor-pointer float-right mt-4 ml-4 h-5 border-l border-surface-950 dark:border-surface-0"></div>
        @if (fullScreenId) {
          <button
            tabindex="1"
            #imageFullScreenDownload
            class="button-remove-style full-screen-download"
            [attr.aria-label]="'cl_chat.download' | translate"
            (click)="downloadFullScreen()"
          >
            <i class="mi2-download text-xl text-surface-950 dark:text-surface-0"></i>
          </button>
        }
      </div>
      <div
        #fullscreenimagecontainer
        class="full-screen-image-container"
        (wheel)="onFullScreenImageMouseWheel($event)"
        (click)="closeTooltip()"
      >
        <div class="full-screen-image-draggable-container">
          <img
            #fullscreenimg
            [ngStyle]="{
              height: fullScreenUseWidth ? 'initial' : fullScreenZoom + '%',
              width: fullScreenUseWidth ? fullScreenZoom + '%' : 'initial'
            }"
            [src]="fullScreenSource"
            cdkDragBoundary=".full-screen-image-draggable-container"
            [cdkDragDisabled]="disableCdkDrag"
            cdkDrag
            alt=""
            [attr.tabindex]="fullScreenId ? 3 : 2"
            [attr.aria-label]="'cl_chat.panImage' | translate"
            (keydown)="onFullScreenImageKeydown($event)"
          />
        </div>
      </div>
      <div class="full-screen-bottom" [ngClass]="{ 'full-screen-slider-show': showTooltip }">
        <button
          [disabled]="isDisableZoomOutButton()"
          [attr.tabindex]="fullScreenId ? 4 : 3"
          class="full-screen-resize-button chat-one-app-button primary-blue"
          [attr.aria-label]="'cl_chat.zoomOut' | translate"
          (click)="fullScreenImageZoomOut()"
          [ngStyle]="{ 'box-shadow': 'var(--p-mi-shadow)' }"
        >
          <i class="mi2-minus text-sm text-surface-0"></i>
        </button>
        <p-slider
          class="full-screen-resize-text w-40"
          [(ngModel)]="fullScreenPercentage"
          [min]="20"
          [max]="300"
          [step]="20"
          (onChange)="sliderChange($event)"
        />
        <button
          #imageFullScreenZoomIn
          [disabled]="isDisableZoomInButton()"
          [attr.tabindex]="fullScreenId ? 5 : 4"
          class="full-screen-resize-button chat-one-app-button primary-blue"
          [attr.aria-label]="'cl_chat.zoomIn' | translate"
          (click)="fullScreenImageZoomIn()"
          [ngStyle]="{ 'box-shadow': 'var(--p-mi-shadow)' }"
        >
          <i class="mi2-add text-sm text-surface-0"></i>
        </button>
      </div>
    </div>
  }
  @if (isFullScreenPdf) {
    <div class="full-screen-content" [ngClass]="{ 'full-screen-in-chat': config?.fullScreenInChat }">
      <div class="full-screen-top">
        <ng-container *ngTemplateOutlet="fullScreenAvatarTemplate"> </ng-container>
        <button
          class="button-remove-style full-screen-close"
          [attr.tabindex]="fullScreenId ? 2 : 1"
          #pdfFullScreenExit
          [attr.aria-label]="'cl_chat.close' | translate"
          (click)="exitFullScreen()"
        >
          <i class="mi2-close text-xl text-surface-950 dark:text-surface-0"></i>
        </button>
        <div class="cursor-pointer float-right mt-4 ml-4 h-5 border-l border-surface-950 dark:border-surface-0"></div>
        @if (fullScreenId) {
          <button
            tabindex="1"
            #pdfFullScreenDownload
            class="button-remove-style full-screen-download"
            [attr.aria-label]="'cl_chat.download' | translate"
            (click)="downloadFullScreen()"
          >
            <i class="mi2-download text-xl text-surface-950 dark:text-surface-0"></i>
          </button>
        }
      </div>
      @if (fullScreenSource) {
        <span #pdfAttachment class="full-screen-pdf">
          <!-- <object *ngIf="chatCloudLinkService.browser.name === 'safari'" class="pdf-embed" [data]="fullScreenPdfSource" type="application/pdf">
        </object> -->
          <iframe class="pdf-embed" [src]="fullScreenSource"></iframe>
        </span>
      }
    </div>
  }
</div>
@if (chatCloudLinkService.browser.os.toLowerCase() === 'ios') {
  <input #iphoneInput class="iphone-input" />
}
<input
  id="fileSelector"
  #attachmentInput
  type="file"
  [hidden]="true"
  [attr.aria-label]="'cl_chat.sendFile' | translate"
  (change)="onFileAttach($event)"
  multiple
/>
<a
  #fullscreendownload
  [hidden]="true"
  [attr.aria-label]="'cl_chat.download' | translate"
  [href]="fullScreenSource"
  download="{{ fullScreenName }}"
></a>

<ng-template #loadingTemplate>
  <div
    [ngClass]="{
      'error-loading-template': (!conversationId && !sdkError()) || sdkError() || error()
    }"
  >
    <div class="ph-item">
      <div class="ph-avatar"></div>
      <div class="ph-row">
        <div class="ph-col-2"></div>
        <div class="ph-col-10 empty"></div>
        <div class="ph-col-4 big"></div>
        <div class="ph-col-8 big empty"></div>
      </div>
    </div>
    <div class="ph-item">
      <div class="ph-avatar"></div>
      <div class="ph-row">
        <div class="ph-col-1"></div>
        <div class="ph-col-11 empty"></div>
        <div class="ph-col-8 big"></div>
        <div class="ph-col-4 big empty"></div>
      </div>
    </div>
    <div class="ph-item">
      <div class="ph-avatar"></div>
      <div class="ph-row">
        <div class="ph-col-1"></div>
        <div class="ph-col-11 empty"></div>
        <div class="ph-col-10 big"></div>
        <div class="ph-col-2 big empty"></div>
      </div>
    </div>
    <div class="ph-item">
      <div class="ph-avatar"></div>
      <div class="ph-row">
        <div class="ph-col-2"></div>
        <div class="ph-col-10 empty"></div>
        <div class="ph-col-6 big"></div>
        <div class="ph-col-6 big empty"></div>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #fullScreenAvatarTemplate>
  <div class="full-screen-avatar">
    <mi-avatar
      [hash]="itemCreateUserId | hash"
      [name]="itemCreateUserName"
      [initials]="itemCreateUserName | initials"
      [photoUrl]="itemCreateUserAvatarSrc"
    >
    </mi-avatar>
    <div>
      <div class="user-info-name-date">
        <span class="user-name">{{ itemCreateUserName }}</span>
        @if (!isMobile) {
          <span class="send-time">{{ itemCreateTime | chatDate: dateTimeLocale : config?.showDayOfWeek }}</span>
        }
      </div>
      @if (!isMobile) {
        <span class="image-name">{{ itemName }}</span>
      }
      @if (isMobile) {
        <span class="image-name">{{ itemName | truncate: 35 }}</span>
      }
    </div>
  </div>
</ng-template>
